import {BanTypeEnum, BanGroupEnum, BanTypeToGroupMap, BanTypeDisplayOrder, hebrewMonths} from '../Constants/banTypeEnum';
import {mapPaymentDataFromBackend} from '../Constants/banPaymentDataTypeEnum';

export const mapBackendToClientModel = (banBackendData) => {
  return {
    // Existing fields (keep as-is)
    idNumber: banBackendData.idNumber,
    additionalIdNumber: banBackendData.banAdditionalId,
    banNumber: banBackendData.banNumber,
    name: banBackendData.name,
    isEnergy: banBackendData.isEnergy ?? false,
    productDescriptions: banBackendData.productDescriptions ?? '',
    hasDebt: banBackendData.hasDebt ?? false,
    hasPaymentMethodIssue: banBackendData.hasPaymentMethodIssue ?? false,

    amount: parseFloat(banBackendData.debtAmount ?? 0),
    pastDueAmt: parseFloat(banBackendData.pastDueAmt ?? 0),
    totalDueAmount: parseFloat(banBackendData.totalDueAmount),
    arBalance: parseFloat(banBackendData.arBalance),
    futureAmount: parseFloat(banBackendData.futureAmount ?? 0),

    paused: banBackendData.paused ?? false,
    payPendingStatus: banBackendData.payPendingStatus ?? false,

    billCycle: banBackendData.billCycle,
    billDueDate: banBackendData.billDueDate,
    prdCvrgEndDateByBillCycle: getrdCvrgEndDate(banBackendData.billCycle),
    prdCvrgEndDate: banBackendData.prdCvrgEndDate,
    prdCvrgStartDate: banBackendData.prdCvrgStartDate,
    arCashInd: banBackendData.arCashInd,
    invoiceUrl: getClearUrl(banBackendData.invoiceUrl),
    paymentData: mapPaymentDataFromBackend(banBackendData.paymentData[0]),
    previousPaymentData: banBackendData.paymentData[1] ? mapPaymentDataFromBackend(banBackendData.paymentData[1]) : null,
  };
};

function getrdCvrgEndDate(billCycle) {
  const dayBeforeBillCycle = billCycle - 1;
  const today = new Date();
  const currentMonth = today.getMonth();
  const endDate = new Date(today.getFullYear(), currentMonth, dayBeforeBillCycle);
  endDate.setHours(0, 0, 0, 0);

  if (endDate >= today) {
    //return date where day=dayBeforeBillCycle mm=currentMonth - 1 month
    const cvrgEndDate = new Date(today.getFullYear(), currentMonth - 1, dayBeforeBillCycle);
    //return in string format dd/mm/yyyy
    return cvrgEndDate.toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit', year: 'numeric'});
  } else {
    //return in string format dd/mm/yyyy
    return endDate.toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit', year: 'numeric'});
  }
}

/**
 * Parses a date string in dd/mm/yyyy format and returns a Date object
 * @param {string} dateString - Date string in dd/mm/yyyy format
 * @returns {Date|null} Date object or null if invalid
 */
const parseDDMMYYYY = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return null;

  const parts = dateString.split('/');
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
  const year = parseInt(parts[2], 10);

  // Validate the parsed values
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

  const date = new Date(year, month, day);
  date.setHours(0, 0, 0, 0);

  // Verify the date is valid (e.g., not 31/02/2024)
  if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
    return null;
  }

  return date;
};

/**
 * Determines the Ban type based on backend response data
 * @param {Object} ban - Ban/Ban object from backend
 * @param {number} maxPaymentAmount - Maximum payment amount threshold
 * @returns {number} BanTypeEnum value (1-7)
 */
export const determineBanType = (ban, paymentConfiguration) => {
  const {hasDebt, hasPaymentMethodIssue, isEnergy = false} = ban;

  const {maxPayment, maxPaymentEnergy} = paymentConfiguration;

  const debtAmount = parseFloat(ban?.amount) || 0;

  // Type 7: Customer with debt above maximum payment limit
  // Condition: hasDebt = true + debt_amount >= maxPaymentAmount.
  if (hasDebt && debtAmount > 0 && ((!isEnergy && debtAmount >= maxPayment) || (isEnergy && debtAmount >= maxPaymentEnergy))) {
    return BanTypeEnum.BAN_WITH_DEBT_ABOVE_MAX;
  }

  // Type 1: Customer with debt
  // Condition: hasDebt = TRUE + debt_amount < maxPaymentAmount
  if (hasDebt && debtAmount > 0 && ((!isEnergy && debtAmount < maxPayment) || (isEnergy && debtAmount < maxPaymentEnergy))) {
    return BanTypeEnum.BAN_WITH_DEBT;
  }

  // Types 2 and 3–6: no debt. Financial shape (3–6) is classifyNonDebtFinancialBanType — ignores payment method issue.
  // - If hasPaymentMethodIssue → type 2 (PaymentIssueBanStatus resolves 3–6 for display via the same classifier).
  // - Else → return that financial type when it matches.
  if (!hasDebt) {
    const nonDebtFinancial = classifyNonDebtFinancialBanType(ban, paymentConfiguration);
    if (hasPaymentMethodIssue) {
      return BanTypeEnum.BAN_WITHOUT_PAYMENT_METHOD;
    }
    if (nonDebtFinancial) {
      return nonDebtFinancial;
    }
  }

  return null;
};

/**
 * Checks if today falls between prd_cvrg_end_date and (bill_due_date - 1 day)
 * @param {string} prdCvrgEndDate - Period coverage end date in dd/mm/yyyy format
 * @param {string} billDueDate - Bill due date in dd/mm/yyyy format
 * @returns {boolean}
 */
export const isWithinInvoicePeriod = (prdCvrgEndDate, yDaysCount, billDueDate) => {
  if (!prdCvrgEndDate || !billDueDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = parseDDMMYYYY(prdCvrgEndDate);
  if (!startDate) return false;
  startDate.setDate(startDate.getDate() + yDaysCount);
  const endDate = parseDDMMYYYY(billDueDate);
  if (!endDate) return false;
  endDate.setDate(endDate.getDate() - 1); // One day before bill_due_date
  return today >= startDate && today <= endDate;
};

/**
 * Checks if today falls within X days after bill_due_date
 * @param {string} billDueDate - Bill due date in dd/mm/yyyy format
 * @param {number} xDays - Number of days after due date
 * @returns {boolean}
 */
const isWithinDaysAfterDueDate = (billDueDate, xDays) => {
  if (!billDueDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDate = parseDDMMYYYY(billDueDate);
  if (!dueDate) return false;

  const endDate = new Date(dueDate);
  endDate.setDate(endDate.getDate() + xDays);

  return today >= dueDate && today <= endDate;
};

/**
 * Type 5 date condition:
 * - If no debt in last invoice: Today falls between bill_due_date and (next prd_cvrg_strt_date + Y days)
 * - If had debt in last invoice: Today falls X days after bill_due_date
 * @param {string} billDueDate - Bill due date in dd/mm/yyyy format
 * @param {string} nextPrdCvrgStartDate - Next period coverage start date in dd/mm/yyyy format
 * @param {number} pastDueAmt - Debt amount from last invoice
 * @returns {boolean}
 */

const isType5DateConditionMet = (billDueDate, pastDueAmt, xDaysCount) => {
  if (!billDueDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDate = parseDDMMYYYY(billDueDate);
  if (!dueDate) return false;

  const dueDateMinusOneMonth = new Date(dueDate);
  dueDateMinusOneMonth.setMonth(dueDateMinusOneMonth.getMonth() - 1);

  if (pastDueAmt <= 0) {
    // No debt in last invoice: customer enters on billing day (bill_due_date - one month ago) or after
    return today >= dueDateMinusOneMonth;
  } else if (pastDueAmt > 0) {
    // Had debt in last invoice: today falls X days after bill_due_date
    dueDateMinusOneMonth.setDate(dueDateMinusOneMonth.getDate() + xDaysCount);
    return today >= dueDateMinusOneMonth;
  }

  return false;
};

/**
 * Types 3–6 when the customer has no debt. Does not use hasPaymentMethodIssue.
 * (When !hasPaymentMethodIssue, determineBanType returns this; when hasPaymentMethodIssue,
 * PaymentIssueBanStatus uses this for the secondary “ban information” block.)
 */
export const classifyNonDebtFinancialBanType = (ban, paymentConfiguration) => {
  const {billDueDate, prdCvrgEndDate, isEnergy = false} = ban;
  const {paymentStatusDisplayDaysX, paymentStatusDisplayDaysY, energyPaymentStatusDisplayDaysX, energyPaymentStatusDisplayDaysY} =
    paymentConfiguration;

  const arBalance = parseFloat(ban?.arBalance) || 0;
  const totalDue = parseFloat(ban?.totalDueAmount) || 0;
  const pastDueAmt = parseFloat(ban?.pastDueAmt) || 0;

  const xDaysCount = isEnergy ? energyPaymentStatusDisplayDaysX : paymentStatusDisplayDaysX;
  const yDaysCount = isEnergy ? energyPaymentStatusDisplayDaysY : paymentStatusDisplayDaysY;

  if (arBalance <= 0 && pastDueAmt > 0) {
    if (isWithinDaysAfterDueDate(billDueDate, xDaysCount)) {
      return BanTypeEnum.BAN_NO_DEBT_BUT_HAD_DEBT_IN_INVOICE;
    }
  }

  if (isWithinInvoicePeriod(prdCvrgEndDate, yDaysCount, billDueDate) && arBalance !== totalDue) {
    return BanTypeEnum.BAN_WITH_BALANCE_FOR_CHARGING;
  }
  if (isWithinInvoicePeriod(prdCvrgEndDate, yDaysCount, billDueDate)) {
    return BanTypeEnum.BAN_WITH_FUTURE_INVOICE;
  }
  if (arBalance <= 0 && isType5DateConditionMet(billDueDate, pastDueAmt, xDaysCount)) {
    return BanTypeEnum.BAN_NO_DEBT_NO_PREVIOUS_DEBT;
  }

  return null;
};

/**
 * Computes the next invoice payment date for Type 5: prd_cvrg_end_date + 1 month + Y days
 * @param {string} prdCvrgEndDate - Period coverage end date in dd/mm/yyyy format
 * @param {number} yDaysCount - Number of extra days (Y parameter from config)
 * @returns {string|null} Date string in dd/mm/yyyy format or null if invalid
 */
const computeType5NextPaymentDate = (prdCvrgEndDate, yDaysCount) => {
  const endDate = parseDDMMYYYY(prdCvrgEndDate);
  if (!endDate) return null;

  endDate.setMonth(endDate.getMonth() + 1);
  endDate.setDate(endDate.getDate() + yDaysCount);

  const day = String(endDate.getDate()).padStart(2, '0');
  const month = String(endDate.getMonth() + 1).padStart(2, '0');
  const year = endDate.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Type 2 UI: same 3–6 classification as determineBanType uses (without payment-issue guard) + type 5 date for copy.
 * @returns {{ informationType: number|null, nextPaymentDueDate: string|null }}
 */
export const getNonDebtFinancialDisplayContext = (ban, paymentConfiguration) => {
  const informationType = classifyNonDebtFinancialBanType(ban, paymentConfiguration);
  if (!informationType) {
    return {informationType: null, nextPaymentDueDate: null};
  }
  if (informationType === BanTypeEnum.BAN_NO_DEBT_NO_PREVIOUS_DEBT) {
    const yDaysCount = ban.isEnergy ? paymentConfiguration.energyPaymentStatusDisplayDaysY : paymentConfiguration.paymentStatusDisplayDaysY;
    return {
      informationType,
      nextPaymentDueDate: computeType5NextPaymentDate(ban.prdCvrgEndDate, yDaysCount),
    };
  }
  return {informationType, nextPaymentDueDate: null};
};

/**
 * Groups Bans by their Ban type
 * @param {Array} Bans - Array of Ban objects with BanType property
 * @returns {Object} Object with groups as keys and arrays of Bans as values
 */
export const groupBansByType = (bans) => {
  const grouped = {
    [BanGroupEnum.FINANCIAL_ISSUES]: [],
    [BanGroupEnum.NO_FINANCIAL_ISSUES]: [],
  };

  bans.forEach((ban) => {
    if (ban.banType) {
      const group = BanTypeToGroupMap[ban.banType];
      if (group) {
        grouped[group].push(ban);
      }
    }
  });

  return grouped;
};

const daysInCalendarMonth = (year, monthIndex) => new Date(year, monthIndex + 1, 0).getDate();

/**
 * Next calendar occurrence of billing day-of-month (billCycle) relative to today.
 * billCycle is a day number (e.g. 7, 14, 28); clamped to the length of the target month.
 * @param {number|string|undefined|null} billCycle
 * @param {Date} today - Start of local day
 * @returns {Date}
 */
const getNextBillCycleOccurrenceDate = (billCycle, today) => {
  const day = Number(billCycle);
  if (!Number.isFinite(day) || day < 1) {
    return new Date(8640000000000000);
  }
  const billingDay = Math.trunc(day);
  let year = today.getFullYear();
  let month = today.getMonth();

  const dateInMonth = (y, m) => {
    const dim = daysInCalendarMonth(y, m);
    const d = Math.min(billingDay, dim);
    const dt = new Date(y, m, d);
    dt.setHours(0, 0, 0, 0);
    return dt;
  };

  let next = dateInMonth(year, month);
  if (next <= today) {
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
    next = dateInMonth(year, month);
  }
  return next;
};

/**
 * Sorts Bans within a group: first by ban type priority, then by billCycle within the same type.
 * @param {Array} bans - Array of Ban objects
 * @returns {Array} Sorted array of Bans
 */
export const sortBansInGroup = (bans) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return bans.sort((a, b) => {
    const typeOrderA = BanTypeDisplayOrder[a.banType] ?? 999;
    const typeOrderB = BanTypeDisplayOrder[b.banType] ?? 999;
    if (typeOrderA !== typeOrderB) return typeOrderA - typeOrderB;

    const dateA = getNextBillCycleOccurrenceDate(a.billCycle, today);
    const dateB = getNextBillCycleOccurrenceDate(b.billCycle, today);
    const diff = dateA.getTime() - dateB.getTime();
    if (diff !== 0) return diff;

    const numA = String(a.banNumber ?? '');
    const numB = String(b.banNumber ?? '');
    return numA.localeCompare(numB, undefined, {numeric: true});
  });
};

/**
 * Processes and sorts all Bans into groups
 * @param {Array} bans - Array of raw Ban objects from backend
 * @param {number} maxPaymentAmount - Maximum payment amount threshold
 * @returns {Object} Object with sorted groups and display configuration
 */
export const processAndGroupBans = (bans, paymentConfiguration) => {
  // Step 1: Classify each Ban by type
  const classifiedBans = bans
    .map((ban) => {
      const banType = determineBanType(ban, paymentConfiguration);
      const enrichedBan = {...ban, banType};

      if (banType === BanTypeEnum.BAN_NO_DEBT_NO_PREVIOUS_DEBT) {
        const yDaysCount = ban.isEnergy
          ? paymentConfiguration.energyPaymentStatusDisplayDaysY
          : paymentConfiguration.paymentStatusDisplayDaysY;
        enrichedBan.nextPaymentDueDate = computeType5NextPaymentDate(ban.prdCvrgEndDate, yDaysCount);
      }

      return enrichedBan;
    })
    .filter((ban) => ban.banType !== null); // Filter out Bans without valid type

  // Step 2: Group by Ban group (A or B)
  const grouped = groupBansByType(classifiedBans);

  // Step 3: Sort within each group
  grouped[BanGroupEnum.FINANCIAL_ISSUES] = sortBansInGroup(grouped[BanGroupEnum.FINANCIAL_ISSUES]);
  grouped[BanGroupEnum.NO_FINANCIAL_ISSUES] = sortBansInGroup(grouped[BanGroupEnum.NO_FINANCIAL_ISSUES]);

  // Step 4: Determine display configuration
  const hasFinancialIssues = grouped[BanGroupEnum.FINANCIAL_ISSUES].length > 0;
  const hasNoFinancialIssues = grouped[BanGroupEnum.NO_FINANCIAL_ISSUES].length > 0;
  const hasBothGroups = hasFinancialIssues && hasNoFinancialIssues;

  return {
    ...grouped,
    displayConfig: {
      hasBothGroups,
      shouldCollapseGroup2: hasBothGroups, // Group 2 should be collapsible only when both groups exist
    },
  };
};

/**
 * Flattens grouped Bans into a single sorted array
 * Group A (Financial Issues) comes first, then Group B (No Financial Issues)
 * @param {Object} groupedBans - Result from processAndGroupBans (includes group arrays and displayConfig)
 * @returns {Array} Flattened array of all bans
 */
export const flattenGroupedBans = (groupedBans) => {
  return [...groupedBans[BanGroupEnum.FINANCIAL_ISSUES], ...groupedBans[BanGroupEnum.NO_FINANCIAL_ISSUES]];
};

/**
 * Determines if Group 2 (No Financial Issues) should be displayed in a collapsible section
 * Display logic:
 * - If customer has accounts from both groups → Group 1 displayed open, Group 2 in collapsible section (closed by default)
 * - If customer has accounts from one group only → All displayed open
 * @param {Object} groupedBans - Result from processAndGroupBans
 * @returns {boolean} True if Group 2 should be collapsible
 */
export const shouldDisplayGroup2AsCollapsible = (groupedBans) => {
  return groupedBans?.displayConfig?.shouldCollapseGroup2 ?? false;
};

/**
 * Title logic:
 * 1. If at least one account has debt (Type 1 or 7) → "יש פה חוב להסדיר"
 * 2. If no debts but at least one account needs payment method update (Type 2) → "יש פה עניין לטפל"
 * 3. Default → "מצב החשבונות שלך" (multiple accounts) or "מצב החשבון שלך" (single account)
 */
export const determineScreenTitle = (bans, epiData) => {
  // Check if there's at least one account with debt
  const hasDebtAccount = bans.some(
    (ban) => ban.banType === BanTypeEnum.BAN_WITH_DEBT || ban.banType === BanTypeEnum.BAN_WITH_DEBT_ABOVE_MAX,
  );

  if (hasDebtAccount) {
    //get from state epiData thereIsDebtSubTitle
    return epiData.thereIsDebtSubTitle;
  }

  // Check if there's at least one account with payment method issue (and no debts)
  const hasPaymentMethodIssue = bans.some((ban) => ban.banType === BanTypeEnum.BAN_WITHOUT_PAYMENT_METHOD);

  if (hasPaymentMethodIssue) {
    //get from state epiData noDebtThereIsPaymentIssueSubTitle
    return epiData.noDebtThereIsPaymentIssueSubTitle;
  }
  return '';
};

export const determineImageName = (bans) => {
  const hasDebtAccount = bans.some(
    (ban) => ban.banType === BanTypeEnum.BAN_WITH_DEBT || ban.banType === BanTypeEnum.BAN_WITH_DEBT_ABOVE_MAX,
  );
  const hasPaymentMethodIssue = bans.some((ban) => ban.banType === BanTypeEnum.BAN_WITHOUT_PAYMENT_METHOD);

  if (hasDebtAccount || hasPaymentMethodIssue) {
    return 1;
  }

  return 2;
};

export const determinePausedTooltipText = (ban) => {
  if (ban.hasDebt && ban.hasPaymentMethodIssue) {
    return `להחזרת החשבון למצב פעיל יש להסדיר את </br> החוב ולעדכן אמצעי תשלום`;
  }
  if (ban.hasDebt && !ban.hasPaymentMethodIssue) {
    return `להחזרת החשבון למצב פעיל יש</br> להסדיר את החוב `;
  }
  if (!ban.hasDebt && ban.hasPaymentMethodIssue) {
    return `להחזרת החשבון למצב פעיל יש</br>  לעדכן אמצעי תשלום`;
  }
};

export const futurePaymentScheduledDate = (banData) => {
  if (!banData.billDueDate) return null;

  const parts = banData.prdCvrgEndDate.split('/');
  if (parts.length !== 3) return null;

  const month = parseInt(parts[1], 10) - 1; // 0-indexed
  const year = parts[2].slice(-2); // Last 2 digits

  if (month < 0 || month > 11) return null;

  return {
    invoicePart: `חשבונית ${hebrewMonths[month]} ${year}`,
    chargeText: 'תשלח לחיוב',
    chargeDateText: `ב-${formatDateWithDots(banData.billDueDate)}`,
  };
};

export const scheduledDate = (banData, yDaysCount = 0) => {
  if (!banData.billDueDate) return '';

  switch (banData.banType) {
    case BanTypeEnum.BAN_WITH_FUTURE_INVOICE:
      // Parse dd/mm/yyyy format
      const parts = banData.prdCvrgEndDate.split('/');
      if (parts.length !== 3) return '';
      const month = parseInt(parts[1], 10) - 1; // 0-indexed
      const year = parts[2].slice(-2); // Last 2 digits
      if (month < 0 || month > 11) return '';

      return `חשבונית ${hebrewMonths[month]} ${year} תשלח לחיוב ב-${formatDateWithDots(banData.billDueDate)}`;
    case BanTypeEnum.BAN_WITH_BALANCE_FOR_CHARGING:
      return `יתרת התשלום תשלח לחיוב<br/> ב-${formatDateWithDots(banData.billDueDate)}`;
    case BanTypeEnum.BAN_NO_DEBT_NO_PREVIOUS_DEBT: {
      const nextBill = parseDDMMYYYY(banData.prdCvrgEndDate);
      let nextBillMonthIdx = nextBill.getMonth(); // 0–11 like getMonth()
      const nextBillMonthYear = String(nextBill.getFullYear()).slice(-2);

      const prdCvrgEndDateByBillCycleParsed = parseDDMMYYYY(banData.prdCvrgEndDateByBillCycle);
      prdCvrgEndDateByBillCycleParsed.setMonth(prdCvrgEndDateByBillCycleParsed.getMonth() + 1);
      prdCvrgEndDateByBillCycleParsed.setDate(prdCvrgEndDateByBillCycleParsed.getDate() + yDaysCount);
      const nextMonthIdx = prdCvrgEndDateByBillCycleParsed.getMonth(); // 0–11 like getMonth()
      const nextDay = String(prdCvrgEndDateByBillCycleParsed.getDate()).padStart(2, '0');
      let nextMonthNum = String(nextMonthIdx).padStart(2, '0'); // 1–12 for dd/mm/yyyy

      const bill_due_date = banData.billDueDate;
      const billDueDate = parseDDMMYYYY(bill_due_date);
      //check if today > bill_due_date
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (!billDueDate) return '';
      if (today >= billDueDate) {
        nextBillMonthIdx = nextBillMonthIdx + 1;
        nextMonthNum = String(nextMonthIdx + 1).padStart(2, '0'); // 1–12 for dd/mm/yyyy
      }
      const nextOpenDateStr = `${nextDay}/${nextMonthNum}/${prdCvrgEndDateByBillCycleParsed.getFullYear()}`;

      return `חשבונית ${hebrewMonths[nextBillMonthIdx]} ${nextBillMonthYear} תהיה פתוחה לתשלום החל מ-${formatDateWithDots(
        nextOpenDateStr,
      )}`;
    }

    case BanTypeEnum.BAN_NO_DEBT_BUT_HAD_DEBT_IN_INVOICE:
      const billDueDate = parseDDMMYYYY(banData.billDueDate);
      if (!billDueDate) return '';
      const billMonth = billDueDate.getMonth();
      const billYear = String(billDueDate.getFullYear()).slice(-2);
      return {
        sent: `חשבון נשלח לחיוב ב-${formatDateWithDots(banData.billDueDate)}`,
        invoice: `חשבונית ${hebrewMonths[billMonth]} ${billYear} בסך`,
      };
    default:
      return '';
  }
};

export const formatDateWithDots = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return '';
  return dateString.replace(/\//g, '.');
};

/** Formats a monetary value for display only (always two digits after the decimal point). */
export const formatAmount = (value) => {
  if (value == null || value === '') return '';
  const n = typeof value === 'number' ? value : parseFloat(String(value).trim().replace(/,/g, ''));
  if (Number.isNaN(n)) return '';
  return n.toFixed(2);
};

export const getClearUrl = (url) => {
  if (!url) return '';
  if (url.includes('_NotC')) {
    return url.replace('_NotC', '');
  } else {
    return url;
  }
};
